import { useState } from "react";
import {
  DndContext, PointerSensor, useSensor, useSensors,
  useDraggable, useDroppable, DragOverlay, closestCorners,
} from "@dnd-kit/core";
import { cn } from "@/lib/utils";
import { Avatar } from "@/components/shared/UI";
import { formatSize, formatDateShort } from "@/lib/format";
import { AlertTriangle, CalendarClock, GripVertical, Plus } from "lucide-react";
import { useAttachmentCounts, AttachmentCount } from "@/components/shared/AttachmentCount";

function linkLine(opp) {
  return [...(opp.organisation_names || []), ...(opp.property_names || [])].join(", ");
}

function DealCard({ opp, onOpen, overlay }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: opp.id });
  const links = linkLine(opp);
  return (
    <div
      ref={overlay ? undefined : setNodeRef}
      data-testid={overlay ? undefined : `deal-card-${opp.id}`}
      onClick={() => !overlay && onOpen(opp.id)}
      style={opp.stage_color ? { borderLeftWidth: 3, borderLeftColor: opp.stage_color } : undefined}
      className={cn(
        "group rounded-lg border border-slate-200 bg-white p-3 shadow-xs transition-shadow hover:shadow-md",
        overlay ? "cursor-grabbing rotate-1 shadow-lg" : "cursor-pointer",
        isDragging && !overlay && "opacity-40"
      )}
    >
      <div className="flex items-start gap-1.5">
        {!overlay && (
          <button
            {...listeners}
            {...attributes}
            onClick={(e) => e.stopPropagation()}
            className="mt-0.5 shrink-0 cursor-grab touch-none text-slate-300 hover:text-slate-500 active:cursor-grabbing"
            data-testid={`deal-drag-${opp.id}`}
            aria-label="Drag deal"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-800">{opp.title}</p>
          {links && <p className="truncate text-xs text-slate-400">{links}</p>}
        </div>
      </div>

      {opp.types?.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {opp.types.map((t) => (
            <span key={t} className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">{t}</span>
          ))}
        </div>
      )}

      <div className="mt-2.5 flex items-center justify-between">
        <span className="font-heading text-sm font-bold text-slate-900">{formatSize(opp)}</span>
        {opp.probability != null && <span className="text-[11px] font-medium text-slate-400">{opp.probability}%</span>}
      </div>

      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
          {opp.expected_close_date && (
            <span className="inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" />{formatDateShort(opp.expected_close_date)}</span>
          )}
          <AttachmentCount count={opp.attachment_count} testid={overlay ? undefined : `deal-attach-count-${opp.id}`} />
        </div>
        {opp.owner_name && <Avatar name={opp.owner_name} user className="h-6 w-6 text-[9px]" />}
      </div>

      {opp.flags?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {opp.flags.map((f) => (
            <span key={f} className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
              <AlertTriangle className="h-2.5 w-2.5" />{f}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Column({ stage, opps, onOpen, onAddCard }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });
  return (
    <div className="flex w-72 shrink-0 flex-col" style={{ minHeight: "calc(100vh - 15rem)" }} data-testid={`pipeline-column-${stage.id}`}>
      <div className="mb-2 flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: stage.color || "#0ea5e9" }} />
          <span className="text-sm font-semibold text-slate-700">{stage.name}</span>
          <span className="rounded-full bg-slate-100 px-1.5 text-xs font-medium text-slate-500">{opps.length}</span>
        </div>
      </div>
      <div
        ref={setNodeRef}
        onClick={(e) => { if (e.target === e.currentTarget) onAddCard(stage.id); }}
        className={cn(
          "flex flex-1 flex-col gap-2 rounded-xl border border-transparent p-2 transition-colors",
          isOver ? "border-sky-300 bg-sky-50" : "bg-slate-50/70"
        )}
      >
        {opps.map((o) => <DealCard key={o.id} opp={o} onOpen={onOpen} />)}
        <button
          type="button"
          onClick={() => onAddCard(stage.id)}
          data-testid={`pipeline-add-${stage.id}`}
          className="group/add flex min-h-[64px] flex-1 items-center justify-center rounded-lg border border-dashed border-transparent transition-colors hover:border-slate-300 hover:bg-white/60"
        >
          <span className={cn("inline-flex items-center gap-1 text-xs font-medium", opps.length === 0 ? "text-slate-400" : "text-slate-400 opacity-0 transition-opacity group-hover/add:opacity-100")}>
            <Plus className="h-3.5 w-3.5" /> Add deal
          </span>
        </button>
      </div>
    </div>
  );
}

export function PipelineBoard({ stages, opps, onChange, onOpen, onAddCard }) {
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const attCounts = useAttachmentCounts("opportunity", opps.map((o) => o.id));
  const active = opps.find((o) => o.id === activeId);
  const withCount = (o) => ({ ...o, attachment_count: attCounts[o.id] });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={({ active }) => setActiveId(active.id)}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={({ active, over }) => {
        setActiveId(null);
        if (!over) return;
        const opp = opps.find((o) => o.id === active.id);
        if (opp && opp.stage_id !== over.id) onChange(active.id, over.id);
      }}
    >
      <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-board">
        {stages.map((s) => (
          <Column key={s.id} stage={s} opps={opps.filter((o) => o.stage_id === s.id).map(withCount)} onOpen={onOpen} onAddCard={onAddCard} />
        ))}
      </div>
      <DragOverlay>{active ? <DealCard opp={withCount(active)} overlay /> : null}</DragOverlay>
    </DndContext>
  );
}
